import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import {
    ProblemData,
    PythagoreanQuestionOptions,
    RightTriangleCorner,
    RightTriangleSpec,
} from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { pythagoreanPrompts as en } from "../Files/Prompts/en/pythagorean.ts";
import { pythagoreanPrompts as es } from "../Files/Prompts/es/pythagorean.ts";
import { pythagoreanPrompts as fr } from "../Files/Prompts/fr/pythagorean.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };

/**
 * The Pythagorean theorem with whole-number sides. See ADR 0109.
 *
 * **Every question is built from a Pythagorean triple**, so every answer is a
 * whole number. A triple is drawn by choosing one of five primitives and a
 * whole scale for it that keeps the hypotenuse within the dial, then the
 * question is made from the triple's own sides: no draw is thrown away and
 * redrawn. 9-40-41 is left out because a drawing of its legs, more than 4 to
 * 1, cannot be read (ADR 0109 §4).
 *
 * **The primitive is drawn first, then its scale**, rather than one triple from
 * a list of all of them. A list of every scaled triple up to 100 is half
 * multiples of 3-4-5, and a sheet would look like one triangle at different
 * sizes.
 *
 * **The converse's "no" is a triple with one side moved by 1 or 2.** Every such
 * set is listed and one is picked, so a set that has stopped being a triangle,
 * that has grown past the dial or that has landed on another Pythagorean triple
 * is never in the list to be picked. `PythagoreanGenerator.test.ts` checks that
 * list exhaustively for every triple.
 *
 * **Yes and no are balanced within a page** by the sheet so far: the next
 * converse problem is whichever the page has fewer of, or a coin toss when the
 * page has dealt as many of each.
 */

export const LENGTH_UNIT = "cm";

/** The primitive triples, short leg first (ADR 0109 §4). */
export const PRIMITIVE_TRIPLES: ReadonlyArray<readonly [number, number, number]> = [
    [3, 4, 5],
    [5, 12, 13],
    [8, 15, 17],
    [7, 24, 25],
    [20, 21, 29],
];

export type Triple = [number, number, number];

/** The smallest dial: 3-4-5 scaled once is the only thing smaller than it. */
const SMALLEST_MAX_SIDE = 5;
const DEFAULT_MAX_SIDE = 50;

/** How far a side of a "no" set is moved, and in which directions. */
const NEAR_MISS_SHIFTS = [-2, -1, 1, 2];

function gcd(a: number, b: number): number {
    return b === 0 ? a : gcd(b, a % b);
}

function usableMax(maxSide: number): number {
    return Math.max(SMALLEST_MAX_SIDE, Math.floor(maxSide) || DEFAULT_MAX_SIDE);
}

/** How many whole scales of a primitive keep its hypotenuse within `max`. */
function scalesOf(primitive: readonly [number, number, number], max: number): number {
    return Math.floor(max / primitive[2]);
}

/** Every scaled triple whose hypotenuse is within `max`, in the order of the primitives. */
export function triplesWithin(max: number): Triple[] {
    const triples: Triple[] = [];
    for (const [a, b, c] of PRIMITIVE_TRIPLES) {
        for (let k = 1; k <= scalesOf([a, b, c], max); k++) {
            triples.push([a * k, b * k, c * k]);
        }
    }
    return triples;
}

/** The primitive a triple is a multiple of, and the scale. */
function primitiveOf(triple: Triple): { index: number; scale: number } {
    const scale = gcd(gcd(triple[0], triple[1]), triple[2]);
    const index = PRIMITIVE_TRIPLES.findIndex(
        (p) => p[0] * scale === triple[0] && p[1] * scale === triple[1] && p[2] * scale === triple[2]
    );
    return { index, scale };
}

/**
 * A scaled triple with hypotenuse within `max`, not `avoid`. The primitive is
 * drawn from those that have a triple other than `avoid`, and the scale from
 * one fewer value than there are, stepped over `avoid`'s, so the draw stays
 * uniform and takes two calls.
 */
export function drawTriple(max: number, avoid?: Triple): Triple {
    const avoided = avoid ? primitiveOf(avoid) : null;
    const usable = PRIMITIVE_TRIPLES.map((primitive, index) => ({ primitive, index, scales: scalesOf(primitive, max) }))
        .filter((entry) => entry.scales > 0 && !(avoided && entry.index === avoided.index && entry.scales === 1));
    const { primitive, index, scales } = usable[getRandomInt(0, usable.length - 1)];
    let scale: number;
    if (avoided && avoided.index === index && avoided.scale <= scales) {
        const draw = getRandomInt(1, scales - 1);
        scale = draw >= avoided.scale ? draw + 1 : draw;
    } else {
        scale = getRandomInt(1, scales);
    }
    return [primitive[0] * scale, primitive[1] * scale, primitive[2] * scale];
}

/**
 * The three-side sets that are close to `triple` and are not right triangles
 * (ADR 0109 §4): one side moved by 1 or 2, sorted, that still makes a
 * triangle, stays within `max` and is not itself a Pythagorean triple. Each
 * set once.
 */
export function nearMisses(triple: Triple, max: number): Triple[] {
    const found = new Map<string, Triple>();
    for (let side = 0; side < 3; side++) {
        for (const shift of NEAR_MISS_SHIFTS) {
            const sides = [...triple];
            sides[side] += shift;
            if (sides[side] < 1) continue;
            sides.sort((x, y) => x - y);
            const [x, y, z] = sides;
            if (x + y <= z || z > max || x * x + y * y === z * z) continue;
            found.set(sides.join("-"), [x, y, z]);
        }
    }
    return Array.from(found.values());
}

/** Whether three sides, in increasing order, make a right triangle. */
export function isRightTriangle([x, y, z]: Triple): boolean {
    return x * x + y * y === z * z;
}

function drawnProblem(
    question: PythagoreanQuestionOptions,
    triple: Triple,
    key: number,
    language: PrintedLanguage
): ProblemData {
    const t = PROMPTS[language];
    const [short, long, hypotenuse] = triple;
    // Which leg lies flat is a coin toss, so the longer one is not always the base.
    const flat = getRandomInt(0, 1) === 0;
    const a = flat ? short : long;
    const b = flat ? long : short;
    const corner = getRandomInt(0, 3) as RightTriangleCorner;

    let rightTriangle: RightTriangleSpec;
    let display: string[];
    if (question === PythagoreanQuestionOptions.Hypotenuse) {
        rightTriangle = { a, b, c: hypotenuse, missing: "c", corner };
        display = [t.hypotenuse(), `${hypotenuse} ${LENGTH_UNIT}`];
    } else {
        const missing: "a" | "b" = getRandomInt(0, 1) === 0 ? "a" : "b";
        rightTriangle = { a, b, c: hypotenuse, missing, corner };
        display = [t.missingSide(), `${missing === "a" ? a : b} ${LENGTH_UNIT}`];
    }

    return {
        key,
        numbers: [],
        operator: "",
        display,
        pythagoreanQuestion: question,
        rightTriangle,
        pythagorean: { triple },
    };
}

function converseProblem(triple: Triple, isRight: boolean, max: number, key: number, language: PrintedLanguage): ProblemData {
    const t = PROMPTS[language];
    let sides = triple;
    if (!isRight) {
        const misses = nearMisses(triple, max);
        sides = misses[getRandomInt(0, misses.length - 1)];
    }
    const [x, y, z] = sides;
    const display = [
        t.rightTriangle(x, y, z),
        isRight ? t.yes(x * x, y * y, z * z) : t.no(x * x, y * y, x * x + y * y, z * z),
    ];
    return {
        key,
        numbers: [],
        operator: "",
        display,
        pythagoreanQuestion: PythagoreanQuestionOptions.RightTriangle,
        pythagorean: { triple, sides, isRight },
    };
}

/**
 * One Pythagorean problem. Always succeeds.
 *
 * `avoid` is the triple of the problem before it. `isRight` settles a converse
 * problem's answer; left out, it is a coin toss. `question` is never Mixed:
 * the sheet deals the other three.
 */
export function buildPythagoreanProblem(
    question: PythagoreanQuestionOptions,
    maxSide: number,
    key: number,
    language: PrintedLanguage = "en",
    avoid?: Triple,
    isRight?: boolean
): ProblemData {
    const max = usableMax(maxSide);
    const triple = drawTriple(max, avoid);
    if (question === PythagoreanQuestionOptions.RightTriangle) {
        return converseProblem(triple, isRight ?? getRandomInt(0, 1) === 1, max, key, language);
    }
    return drawnProblem(question, triple, key, language);
}

function questionTypes(question: PythagoreanQuestionOptions): PythagoreanQuestionOptions[] {
    if (question === PythagoreanQuestionOptions.Mixed) {
        return [
            PythagoreanQuestionOptions.Hypotenuse,
            PythagoreanQuestionOptions.MissingLeg,
            PythagoreanQuestionOptions.RightTriangle,
        ];
    }
    return [question];
}

// The same triple asked the same way is the same problem. A converse problem is
// told apart by the sides it prints, since a "no" set shares its triple with
// the "yes" it came from.
function fingerprint(problem: ProblemData): string {
    const data = problem.pythagorean!;
    return `${problem.pythagoreanQuestion}:${(data.sides ?? data.triple).join("-")}`;
}

/** A page-shaped set of Pythagorean theorem problems. */
export function generatePythagoreanPages(settings: ISettingsProps): ProblemData[][] | null {
    const question = settings.PythagoreanQuestion ?? PythagoreanQuestionOptions.Mixed;
    const maxSide = settings.PythagoreanMaxSide ?? DEFAULT_MAX_SIDE;
    const language = printedLanguageOf(settings);
    const perPage = settings.NumberOfProblemsPerPage;
    // The triple and the verdict accepted at each position. `build` is called
    // again at a key until a problem is accepted there, so the last entry for
    // key k is the problem that stays, and key k + 1 avoids it and balances
    // against it.
    const tripleAt = new Map<number, Triple>();
    const verdictAt = new Map<number, boolean>();
    return generateProblemPages({
        settings,
        choices: questionTypes(question),
        dealPerPage: true,
        build: (type, key) => {
            let isRight: boolean | undefined;
            if (type === PythagoreanQuestionOptions.RightTriangle) {
                // The converse is half yes and half no on a page, as far as its count allows.
                let yes = 0;
                let no = 0;
                for (let k = Math.floor((key - 1) / perPage) * perPage + 1; k < key; k++) {
                    const verdict = verdictAt.get(k);
                    if (verdict === true) yes++;
                    if (verdict === false) no++;
                }
                isRight = yes === no ? getRandomInt(0, 1) === 1 : yes < no;
            }
            const problem = buildPythagoreanProblem(type, maxSide, key, language, tripleAt.get(key - 1), isRight);
            tripleAt.set(key, problem.pythagorean!.triple);
            if (isRight === undefined) {
                verdictAt.delete(key);
            } else {
                verdictAt.set(key, isRight);
            }
            return problem;
        },
        fingerprint,
    });
}
